import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation, useOutlet } from 'react-router-dom';
import './PageTransition.css';
import { useSelector, useDispatch } from 'react-redux';
import { useGetMeQuery } from '../../store/api/authApi';
import { logout } from '../../store/slices/authSlice';
import { useTheme } from '../../context/ThemeContext';
import Sidebar from './Sidebar';
import Header from './Header';
import BottomNav from './BottomNav';

const DashboardLayout = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isAuthenticated, token } = useSelector((state) => state.auth);
  const { mode } = useTheme();
  const { isLoading, error } = useGetMeQuery(undefined, {
    skip: !token,
  });

  const location = useLocation();
  const scrollRef = useRef(null);

  // ── Cross-fade between sections ────────────────────────────────────────
  //
  // A fade-OUT needs the outgoing page to still be mounted while it animates,
  // and React unmounts it the instant the route changes. <Outlet /> always
  // renders the CURRENT route, so it cannot hold the previous page either.
  //
  // useOutlet() hands back the element for the current route as a value, which
  // can be held in state. So: keep rendering the captured element, fade it out,
  // and only swap in the new one once that has finished.
  //
  // No library for this — react-transition-group would be a dependency for
  // ~30 lines.
  const outlet = useOutlet();
  // The live element, read inside the animation handler. A closure over
  // `outlet` would be stale by the time the fade-out ends.
  const outletRef = useRef(outlet);
  outletRef.current = outlet;

  const [shown, setShown] = useState(() => ({ el: outlet, path: location.pathname }));
  const [phase, setPhase] = useState('in');

  const prefersReduced = () =>
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  useEffect(() => {
    if (location.pathname === shown.path) return;

    // Reduced motion: swap straight away, no fade either direction.
    if (prefersReduced()) {
      setShown({ el: outletRef.current, path: location.pathname });
      setPhase('in');
      return;
    }

    setPhase('out');

    // Safety net. The swap normally happens on animationend, but that event is
    // not guaranteed — a background tab throttles rAF, and an element that is
    // display:none when the class lands never animates at all. Without this the
    // page would sit in its faded-out state indefinitely, i.e. a blank screen
    // on a perfectly good navigation.
    //
    // Comfortably longer than the 170ms exit, so it only ever fires when the
    // event genuinely did not arrive.
    const bail = setTimeout(() => {
      setShown({ el: outletRef.current, path: location.pathname });
      setPhase('in');
    }, 400);

    return () => clearTimeout(bail);
  }, [location.pathname, shown.path]);

  const handleAnimationEnd = useCallback(
    (event) => {
      // Only the wrapper's own animation may trigger the swap. The section
      // cascade in PageTransition.css animates child elements, and those
      // animationend events bubble — without this guard the first section
      // finishing would swap the page mid-fade.
      if (event.target !== event.currentTarget) return;
      if (phase !== 'out') return;

      setShown({ el: outletRef.current, path: location.pathname });
      setPhase('in');
    },
    [phase, location.pathname],
  );

  // Reset scroll when the NEW page is swapped in, not when the route changes.
  // Resetting on the route change would scroll the OUTGOING page to the top
  // while it is still visible and fading.
  //
  // Both targets are hit on purpose. In this layout the DOCUMENT is what
  // scrolls: .page-content has overflow-y:auto but its parent uses
  // min-height:100vh, so it grows to fit its content instead of scrolling —
  // window.scrollTo is what actually does the work today. The container call is
  // kept because it costs nothing and is the correct target if .page-content is
  // ever given a definite height (see the note in PageTransition.css).
  //
  // behavior:'instant' so the reset is never animated, whatever
  // scroll-behavior is in force.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) {
      if (typeof el.scrollTo === 'function') {
        el.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      } else {
        el.scrollTop = 0;
      }
    }
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [shown.path]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    // Only a genuine auth failure may end the session. This used to log out on
    // ANY getMe error, so a 429 from the shared auth rate-limit bucket, a 5xx, or
    // a dropped connection dumped a perfectly valid session back at /login —
    // which is what made "Save & exit" out of onboarding look like a logout.
    // A stale profile is not a reason to sign someone out; the token in the store
    // stays authoritative until the server actually rejects it.
    if (error && error.status === 401) {
      dispatch(logout());
      navigate('/login');
    }
  }, [error, dispatch, navigate]);

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner large" />
        <p>Loading...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className={`dashboard-layout ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
      {mode === 'test' && (
        <div className="test-mode-ribbon">
          <span>Test Mode</span>
          <span className="ribbon-info">You are viewing test data. Transactions are simulated.</span>
        </div>
      )}
      <Sidebar
        collapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
        drawerOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      />
      <div className="main-content">
        <Header onMenuClick={() => setDrawerOpen(true)} />
        <main className="page-content" ref={scrollRef}>
          {/* `key` on the wrapper restarts the entrance animation per route.
              `phase` drives which direction it plays. */}
          <div
            className={phase === 'out' ? 'page-leave' : 'page-enter'}
            key={shown.path}
            onAnimationEnd={handleAnimationEnd}
          >
            {/* The captured element is used ONLY while the old page is on its
                way out. Rendering shown.el at all times made it stale whenever
                the location changed without the pathname changing -- a
                ?tab= deep link, a ?page= or filter param, router state -- since
                shown is refreshed on pathname alone. Falling through to the
                live outlet the rest of the time keeps the exit animation and
                removes that whole class of bug. */}
            {phase === 'out' ? shown.el : outlet}
          </div>
        </main>
      </div>
      <BottomNav />
    </div>
  );
};

export default DashboardLayout;
